import { useState, useCallback, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Menu, X } from "lucide-react";
import HabitusPayLogo from "@/components/HabitusPayLogo";

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState<string>("");

  const navLinks = [
    { href: "#soluciones", label: "Soluciones" },
    { href: "#producto", label: "Producto" },
    { href: "#precios", label: "Precios" },
    { href: "#faq", label: "Recursos" },
  ];

  // Track active section on scroll
  useEffect(() => {
    const sectionIds = navLinks.map(link => link.href.replace('#', ''));
    
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSection(`#${entry.target.id}`);
          }
        });
      },
      {
        rootMargin: '-20% 0px -70% 0px',
        threshold: 0
      }
    );

    sectionIds.forEach((id) => {
      const element = document.getElementById(id);
      if (element) observer.observe(element);
    });

    return () => observer.disconnect();
  }, []);

  const handleSmoothScroll = useCallback((e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    e.preventDefault();
    const targetId = href.replace('#', '');
    const element = document.getElementById(targetId);
    
    if (element) {
      const headerOffset = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.scrollY - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
    
    setIsMenuOpen(false);
  }, []);

  return (
    <header 
      className="fixed top-0 left-0 right-0 z-50 bg-background/95 supports-[backdrop-filter]:bg-background/80 supports-[backdrop-filter]:backdrop-blur-md border-b border-border/50"
      role="banner"
    >
      <div className="container mx-auto">
        <div className="flex items-center justify-between h-14 sm:h-16 md:h-20">
          {/* Logo */}
          <a href="/" className="flex items-center" aria-label="HabitusPay - Ir a inicio">
            <HabitusPayLogo variant="full" />
          </a>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-6 xl:gap-8" role="navigation" aria-label="Navegación principal">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={(e) => handleSmoothScroll(e, link.href)}
                aria-current={activeSection === link.href ? "page" : undefined}
                className={`relative text-sm font-medium transition-colors after:content-[''] after:absolute after:w-full after:h-0.5 after:bottom-0 after:left-0 after:bg-accent after:transition-transform after:duration-300 ${
                  activeSection === link.href
                    ? 'text-foreground after:scale-x-100 after:origin-bottom-left'
                    : 'text-muted-foreground hover:text-foreground after:scale-x-0 after:origin-bottom-right hover:after:scale-x-100 hover:after:origin-bottom-left'
                }`}
              >
                {link.label}
              </a>
            ))}
          </nav>

          {/* Desktop CTAs */}
          <div className="hidden lg:flex items-center gap-2 xl:gap-3">
            <Button variant="navOutline" size="sm" asChild>
              <a href="https://app.habituspay.com" target="_blank" rel="noopener noreferrer">
                Iniciar sesión
              </a>
            </Button>
            <Button variant="nav" size="sm" asChild>
              <a href="https://app.habituspay.com" target="_blank" rel="noopener noreferrer">
                Empezar gratis
              </a>
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2 text-foreground -mr-2"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label={isMenuOpen ? "Cerrar menú" : "Abrir menú"}
            aria-expanded={isMenuOpen}
            aria-controls="mobile-menu"
          >
            {isMenuOpen ? <X size={24} aria-hidden="true" /> : <Menu size={24} aria-hidden="true" />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div id="mobile-menu" className="lg:hidden py-4 border-t border-border/50 animate-fade-in" role="dialog" aria-modal="true">
            <nav className="flex flex-col gap-1" role="navigation" aria-label="Navegación móvil">
              {navLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  onClick={(e) => handleSmoothScroll(e, link.href)}
                  className="text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors py-3 px-2 rounded-lg"
                >
                  {link.label}
                </a>
              ))}
              <div className="flex flex-col gap-2 pt-4 mt-2 border-t border-border/50">
                <Button variant="navOutline" size="sm" className="w-full justify-center" asChild>
                  <a href="https://app.habituspay.com" target="_blank" rel="noopener noreferrer">
                    Iniciar sesión
                  </a>
                </Button>
                <Button variant="nav" size="sm" className="w-full justify-center" asChild>
                  <a href="https://app.habituspay.com" target="_blank" rel="noopener noreferrer">
                    Empezar gratis
                  </a>
                </Button>
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
